import type { Schemas } from "@acervo/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import {
  Ban,
  KeyRound,
  LogOut,
  Monitor,
  ShieldOff,
  Tv,
  Terminal,
  Unlock,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import {
  ErrorBox,
  Modal,
  PageHeader,
  SecretBox,
  Spinner,
} from "../../components/ui";
import { api, useSession } from "../../lib/api";
import { dateTime, errorMessage, relative, roleLabel } from "../../lib/format";
import { StatusBadge } from "./users";

const route = getRouteApi("/_app/console/usuarios/$userId");

type Action = "block" | "unblock" | "revoke" | "reset-password" | "reset-mfa";

export function UserDetailPage() {
  const { userId } = route.useParams();
  const me = useSession()!;
  const [action, setAction] = useState<Action | null>(null);
  const q = useQuery({
    queryKey: ["admin", "user", userId],
    queryFn: () =>
      api.unwrap(
        api.client.GET("/v1/admin/users/{id}", {
          params: { path: { id: userId } },
        }),
      ),
  });

  if (q.isPending) return <Spinner />;
  if (q.isError)
    return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  const u = q.data;
  const self = u.id === me.user.id;
  // Admin não mexe no dono nem em outros admins; só o dono pode.
  const canManage =
    !self &&
    u.role !== "owner" &&
    (me.user.role === "owner" || u.role !== "admin");

  return (
    <div className="space-y-6">
      <Link to="/console/usuarios" className="text-xs text-muted hover:text-fg">
        ← Usuários
      </Link>
      <PageHeader
        title={u.display_name || u.email}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {u.email} · {roleLabel[u.role]} <StatusBadge user={u} />
          </span>
        }
        actions={
          canManage && (
            <>
              {u.status === "active" ? (
                <button
                  className="btn-danger"
                  onClick={() => setAction("block")}
                >
                  <Ban className="h-4 w-4" /> Bloquear
                </button>
              ) : (
                <button
                  className="btn-ghost"
                  onClick={() => setAction("unblock")}
                >
                  <Unlock className="h-4 w-4" /> Desbloquear
                </button>
              )}
              <button
                className="btn-ghost"
                onClick={() => setAction("revoke")}
                disabled={u.sessions.length === 0}
              >
                <LogOut className="h-4 w-4" /> Derrubar sessões
              </button>
              <button
                className="btn-ghost"
                onClick={() => setAction("reset-password")}
              >
                <KeyRound className="h-4 w-4" /> Nova senha provisória
              </button>
              {u.mfa_enabled && (
                <button
                  className="btn-ghost"
                  onClick={() => setAction("reset-mfa")}
                >
                  <ShieldOff className="h-4 w-4" /> Remover 2FA
                </button>
              )}
            </>
          )
        }
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <Info
          label="Último acesso"
          value={relative(u.last_login_at)}
          title={dateTime(u.last_login_at)}
        />
        <Info label="Conta criada" value={dateTime(u.created_at)} />
        <Info label="2FA" value={u.mfa_enabled ? "Ativo" : "Desativado"} />
      </div>

      {canManage && (
        <RoleEditor
          userId={u.id}
          role={u.role}
          canAdmin={me.user.role === "owner"}
        />
      )}

      <section>
        <h2 className="mb-3 text-lg font-bold">
          Sessões abertas ({u.sessions.length})
        </h2>
        {u.sessions.length === 0 ? (
          <p className="text-sm text-muted">Nenhuma sessão aberta.</p>
        ) : (
          <div className="card divide-y divide-line">
            {u.sessions.map((s) => (
              <SessionRow key={s.id} s={s} />
            ))}
          </div>
        )}
      </section>

      <Link
        to="/console/auditoria"
        search={{ target: u.id }}
        className="text-sm text-accent hover:underline"
      >
        Ver eventos desta conta na auditoria →
      </Link>

      {action && (
        <ActionDialog
          action={action}
          user={u}
          onClose={() => setAction(null)}
        />
      )}
    </div>
  );
}

function Info({
  label,
  value,
  title,
}: {
  label: string;
  value: string;
  title?: string;
}) {
  return (
    <div className="card p-4" title={title}>
      <p className="text-xs font-medium tracking-wide text-muted uppercase">
        {label}
      </p>
      <p className="mt-1 font-semibold">{value}</p>
    </div>
  );
}

function SessionRow({ s }: { s: Schemas["Session"] }) {
  const Icon =
    s.device_kind === "webos"
      ? Tv
      : s.device_kind === "cli"
        ? Terminal
        : Monitor;
  return (
    <div className="flex items-center gap-3 px-4 py-3 text-sm">
      <Icon className="h-5 w-5 shrink-0 text-muted" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{s.device_name || s.device_kind}</p>
        <p className="truncate text-xs text-muted" title={s.user_agent}>
          {s.ip ?? "IP desconhecido"} · aberta {relative(s.created_at)}
        </p>
      </div>
      <p className="text-right text-xs text-muted">
        usada {relative(s.last_used_at)}
        <br />
        expira {dateTime(s.expires_at)}
      </p>
    </div>
  );
}

const copy: Record<
  Action,
  { title: string; text: string; confirm: string; danger?: boolean }
> = {
  block: {
    title: "Bloquear conta",
    text: "A pessoa sai na hora de todos os aparelhos e não consegue mais entrar até ser desbloqueada.",
    confirm: "Bloquear",
    danger: true,
  },
  unblock: {
    title: "Desbloquear conta",
    text: "A pessoa volta a conseguir entrar.",
    confirm: "Desbloquear",
  },
  revoke: {
    title: "Derrubar sessões",
    text: "Encerra todas as sessões (site, TV, CLI). A pessoa precisará entrar de novo.",
    confirm: "Derrubar",
    danger: true,
  },
  "reset-password": {
    title: "Nova senha provisória",
    text: "Gera uma senha provisória, encerra as sessões e obriga a troca no próximo acesso.",
    confirm: "Gerar senha",
  },
  "reset-mfa": {
    title: "Remover 2FA",
    text: "Use quando a pessoa perdeu o celular e os códigos de recuperação. Confirme com a SUA senha.",
    confirm: "Remover 2FA",
    danger: true,
  },
};

function ActionDialog({
  action,
  user,
  onClose,
}: {
  action: Action;
  user: Schemas["UserDetail"];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [password, setPassword] = useState("");
  const path = { params: { path: { id: user.id } } };
  const m = useMutation({
    mutationFn: async (): Promise<string | null> => {
      switch (action) {
        case "block":
          await api.unwrap(api.client.POST("/v1/admin/users/{id}/block", path));
          return null;
        case "unblock":
          await api.unwrap(
            api.client.POST("/v1/admin/users/{id}/unblock", path),
          );
          return null;
        case "revoke": {
          const r = await api.unwrap(
            api.client.POST("/v1/admin/users/{id}/revoke-sessions", path),
          );
          return `${r.revoked} ${r.revoked === 1 ? "sessão encerrada" : "sessões encerradas"}.`;
        }
        case "reset-password":
          return (
            await api.unwrap(
              api.client.POST("/v1/admin/users/{id}/reset-password", path),
            )
          ).temporary_password;
        case "reset-mfa":
          await api.unwrap(
            api.client.POST("/v1/admin/users/{id}/reset-mfa", {
              ...path,
              body: { password },
            }),
          );
          return null;
      }
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ["admin"] });
      if (!result) onClose();
    },
  });
  const c = copy[action];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    m.mutate();
  };

  if (m.isSuccess && m.data) {
    return (
      <Modal title={c.title} onClose={onClose}>
        <div className="space-y-4">
          {action === "reset-password" ? (
            <>
              <p className="text-sm">
                Senha provisória de <strong>{user.email}</strong>. Ela não será
                mostrada de novo.
              </p>
              <SecretBox value={m.data} />
            </>
          ) : (
            <p className="text-sm">{m.data}</p>
          )}
          <div className="flex justify-end">
            <button className="btn-primary" onClick={onClose}>
              Concluir
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={c.title} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm">
          <strong>{user.display_name || user.email}</strong>: {c.text}
        </p>
        {action === "reset-mfa" && (
          <div>
            <label className="label" htmlFor="my-pw">
              Sua senha
            </label>
            <input
              id="my-pw"
              type="password"
              className="field"
              autoComplete="current-password"
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        )}
        {m.isError && (
          <p className="text-sm text-danger">{errorMessage(m.error)}</p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            className={c.danger ? "btn-danger" : "btn-primary"}
            disabled={m.isPending}
          >
            {m.isPending ? "Aguarde…" : c.confirm}
          </button>
        </div>
      </form>
    </Modal>
  );
}

type EditableRole = "student" | "author" | "family" | "admin";

const roleHelp: Record<EditableRole, string> = {
  student: "Vê os cursos publicados; assiste às aulas liberadas.",
  author: "Como aluno, e no futuro poderá publicar os próprios cursos.",
  family:
    "Vê e assiste a todo o seu acervo, inclusive o conteúdo pessoal. Não administra nada.",
  admin: "Administra usuários e cursos. Exige 2FA.",
};

function RoleEditor({
  userId,
  role,
  canAdmin,
}: {
  userId: string;
  role: string;
  canAdmin: boolean;
}) {
  const qc = useQueryClient();
  const [value, setValue] = useState<EditableRole>(
    (role === "owner" ? "student" : role) as EditableRole,
  );
  const m = useMutation({
    mutationFn: (r: EditableRole) =>
      api.unwrap(
        api.client.POST("/v1/admin/users/{id}/role", {
          params: { path: { id: userId } },
          body: { role: r },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "user", userId] });
      void qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
  const options: EditableRole[] = canAdmin
    ? ["student", "author", "family", "admin"]
    : ["student", "author", "family"];
  return (
    <section className="rounded-xl border border-line p-4">
      <label className="label" htmlFor="role-edit">
        Papel
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <select
          id="role-edit"
          className="field max-w-xs"
          value={value}
          onChange={(e) => setValue(e.target.value as EditableRole)}
        >
          {options.map((r) => (
            <option key={r} value={r}>
              {roleLabel[r]}
            </option>
          ))}
        </select>
        <button
          className="btn-primary"
          disabled={m.isPending || value === role}
          onClick={() => m.mutate(value)}
        >
          {m.isPending ? "Salvando…" : "Salvar papel"}
        </button>
      </div>
      <p className="mt-2 text-xs text-muted">{roleHelp[value]}</p>
      {m.isSuccess && (
        <p className="mt-1 text-xs text-ok">
          Papel alterado. Vale em alguns minutos, ou na hora se a pessoa sair e
          entrar de novo.
        </p>
      )}
      {m.isError && <ErrorBox error={m.error} />}
    </section>
  );
}
